import { Children, cloneElement, isValidElement, type ReactNode } from "react";
import { useLineScope } from "./lineScopeContext";
import { useQuoteDocumentContext } from "./quoteDocumentContext";
import { shouldShowWhen } from "./showWhen";

/** Class marking a block the customer will not see, in the editor only. */
export const HIDDEN_BLOCK_CLASS = "fp-hidden-block";

interface FpShowWhenProps {
  /** The element's `data-fp-show-when` value. */
  condition: string;
  /** The converted element the condition gates. */
  children: ReactNode;
}

/**
 * Gates an element carrying `data-fp-show-when`: the customer-facing render
 * drops it when the condition does not hold, while the editor draws it marked
 * as hidden, so conditional content stays visible and editable instead of
 * vanishing off the canvas. A repeater-stamped block whose condition fails in
 * its own scope but holds at quote scope is dropped even in the editor: a live
 * copy of the same template renders in another stamp, so the grey marker would
 * only duplicate it.
 * @param props - The condition and the converted element it gates
 * @returns The element, marked when hidden, or null in the customer render
 */
export function FpShowWhen({ condition, children }: FpShowWhenProps) {
  const { quote, editable } = useQuoteDocumentContext();
  const scope = useLineScope();
  if (shouldShowWhen(quote, scope, condition)) {
    return <>{children}</>;
  }
  if (!editable) {
    return null;
  }
  if (scope && shouldShowWhen(quote, null, condition)) {
    return null;
  }
  return <>{markHidden(children)}</>;
}

/**
 * Appends the hidden-block class to each element child, leaving other nodes
 * untouched.
 * @param children - The converted element the condition gates
 * @returns The children, marked as hidden for the editor
 */
function markHidden(children: ReactNode): ReactNode {
  return Children.map(children, (child) => {
    if (!isValidElement<{ className?: string }>(child)) {
      return child;
    }
    return cloneElement(child, {
      className: `${child.props.className ?? ""} ${HIDDEN_BLOCK_CLASS}`.trim(),
    });
  });
}
